/* CostCenterCategoriesCrudCard.tsx
	Une card affichant la gestion CRUD des catégories de centres de coûts
	Utilise le composant GenericCrudCard pour lister, ajouter, éditer et supprimer les éléments
	Utilise listPaginated pour bénéficier de la pagination, du tri et des filtres côté serveur
*/

// Import React
import React from 'react';

// Import FontAwesome
import { faCubes } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// Import Communs
import { GenericCrudCard } from 'common';

// Import Feature
import {
	CostCenterCategoryData,
	costCenterCategoriesApi,
} from 'features/costCenterCategories';

// Interface des props du composant
interface CostCenterCategoriesCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
}

// CostCenterCategoriesCrudCard component
export const CostCenterCategoriesCrudCard: React.FC<
	CostCenterCategoriesCrudCardProps
> = ({
	size = 'sm',
	returnPath = '/app/settings/depenses',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';
	// Titre de la card selon la taille
	const cardTitle =
		size === 'sm'
			? 'Catégories de centres de coûts'
			: 'Liste des catégories de centres de coûts';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [
					{
						label: 'ID',
						field: 'id',
						render: (a: CostCenterCategoryData) => a.id,
					},
				]
			: []),
		// Colonne "Libellé"
		{
			label: 'Libellé',
			field: 'label',
			render: (a: CostCenterCategoryData) => a.label,
		},
	];

	return (
		<GenericCrudCard<CostCenterCategoryData>
			queryKey="costCenterCategories"
			title={cardTitle}
			fetchData={(token, options) =>
				costCenterCategoriesApi.listPaginated(token, options)
			}
			deleteItem={costCenterCategoriesApi.delete}
			tableColumns={columns}
			addButtonLabel="Ajouter une catégorie"
			getAddPath={() => '/app/settings/cost-center-categories/new'}
			getEditPath={(id) => `/app/settings/cost-center-categories/${id}/edit`}
			emptyMessage="Aucune catégorie de centre de coûts trouvée."
			getReturnPath={() => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faCubes : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};

export default CostCenterCategoriesCrudCard;
